<template>
<v-card flat class="pb-0 mb-0 pt-0" min-width='100%'>
  <div>
    <v-toolbar class='toolbar' flat style="cursor: default;">
      <v-toolbar-title class="title">Item Master</v-toolbar-title>
      <v-divider class="mx-4" inset vertical></v-divider>
      <v-btn
        v-show="$auth.tenant !== 'alliance-retail-group' && hasWriteAccess"
        color="primary"
        @click="$router.push({ name: 'item', })">
        Create Item
      </v-btn>
      <v-col class="ml-5 px-0" style="max-width: 800px">
        <SearchFilters
          :disabled="loading"
          v-model="searchParams"
          @filterInput="handleFilterInput"
          :originalUpc="originalUpc"
          :suggestedUpc="suggestedUpc"
          @clearUpc="clearUpc"
          @updateUpc="updateUpc"
          @searchSimilar="updateUpc(true)">
        </SearchFilters>
      </v-col>
      <v-col cols="auto" class="pl-0">
        <v-btn
          :loading="loading"
          :disabled="noSearch"
          depressed
          height="40"
          color="accent"
          style="border-top-left-radius: 0px; border-bottom-left-radius:0px; border: 1px solid #9e9e9e; border-left: 0px;"
          @click.stop="submitSearch">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
      </v-col>

      <v-col v-if="$auth.tenant === 'jbg' && isRetailer" cols="auto" class="mr-4">
        <v-switch
          v-model="showDiscontinuedItems"
          label="Show discontinued items"
          color="primary"
          hide-details
          class="my-auto"
          :ripple="false"
          @change="showDiscontinuedItemsChange">
        </v-switch>
      </v-col>

      <v-col v-if="$auth.tenant === 'jbg'" cols="auto" class="ml-auto">
        <v-btn  @click="showPriceHistoryModal = true">
          Price History
          <v-icon right size="large">mdi-tag-outline</v-icon>
        </v-btn>
      </v-col>

      <v-col v-if="['jbg', 'alliance-retail-group'].includes($auth.tenant)" class="my-0 py-0" cols="auto">
        <v-menu
          open-on-hover
          offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              color="primary"
              v-on="on"
              :disabled="Object.entries(searchParams).length === 0">
              Export
              <v-icon class="pl-2 pt-0" size="large">
                mdi-download
              </v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-title class="grey--text text--darken-1">
                Select export type
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item @click.stop="handleExportOptions('simple')">
              <v-list-item-title>Simple</v-list-item-title>
            </v-list-item>
            <v-list-item @click.stop="handleExportOptions('detailed')">
              <v-list-item-title>Detailed</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-toolbar>

    <ItemMasterSplash
      v-if="splashScreen"
      v-model="splashScreen"
      @close="closeSplash">
    </ItemMasterSplash>

    <div class="dataCon">
      <v-form class="tableCon">
        <v-data-table
          id="itemMaster"
          :height="tableSize"
          :headers="computedHeaders"
          :items.sync="items"
          :loading="loading"
          item-key="uuid"
          hide-default-footer
          :items-per-page.sync="pageSize"
          :no-data-text="noDataText"
          fixed-header
          dense
          @contextmenu:row="onRightClick"
          @dblclick:row="viewItem">

          <!-- Discontinued Item Indicator -->
          <template v-slot:[`item.source_name`]="{ item }">
            <span class="text-no-wrap">
              <v-tooltip class="mx-auto" v-if="showDiscontinuedItems && ['DISCONTINUED ITEM'].includes(item.status)" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    color="warning"
                    class="pb-1 ml-n8">
                    <v-icon class="mx-auto">mdi-alert</v-icon>
                  </v-btn>
                </template>
                <span>Item has been Discontinued</span>
              </v-tooltip>
              {{ item.source_name }}
            </span>
          </template>

          <template v-slot:[`item.sale_price`]="{ item }">
            <td class="text-no-wrap">
              {{ item.sale_price == 0 ? '-' : `${item.sale_price ? '$' + item.sale_price.toFixed(2) : '-'}` }}
            </td>
          </template>
          <template v-slot:[`item.cost`]="{ item }">
            <td class="text-no-wrap">{{item.cost ? '$' + item.cost.toFixed(2) : '-'}}</td>
          </template>
          <template v-slot:[`item.weight`]="{ item }">
            <td class="text-no-wrap">{{item.weight ? item.weight.toFixed(2) : '-'}}</td>
          </template>
          <template v-slot:[`item.cube`]="{ item }">
            <td class="text-no-wrap">{{item.cube ? item.cube.toFixed(2) : '-'}}</td>
          </template>
          <template v-slot:[`item.components`]="{ item }">
            <v-icon v-if="Array.isArray(item.components) && item.components.length > 0" @click="openShipperModal(item)">mdi-view-list</v-icon>
          </template>
          <template v-slot:footer>
            <PagingFooter
              :pageSizes="pageSizes"
              :pageSize.sync="pageSize"
              :page="page"
              :disablePrevious="disablePrevious"
              :disableNext="disableNext"
              @previous="previous"
              @next="next">
            </PagingFooter>
          </template>
        </v-data-table>
      </v-form>
      <ShipperComponentModal
        v-model="shipperModal"
        :selectedItem="selectedItem"
        @closeDialog="closeShipperModal">
      </ShipperComponentModal>
      <ExportDetailsModal
        v-if="showDetailsModal"
        v-model="showDetailsModal"
        :selectedItemDetails.sync="selectedItemDetails"
        :isRetailer="isRetailer"
        @exportItems="exportItems"
        @closeDialog="showDetailsModal = false">
      </ExportDetailsModal>
      <PriceHistoryModal
        v-if="showPriceHistoryModal"
        v-model="showPriceHistoryModal"
        @closeDialog="showPriceHistoryModal = false">
      </PriceHistoryModal>
    </div>
  </div>
</v-card>
</template>
<script>
// API
import Items from '@/api/endpoints'
// Components
import PagingFooter from '@/components/PagingFooter.vue'
import SearchFilters from '@/components/items/SearchFilters.vue'
import ItemMasterSplash from '@/components/items/itemMasterSplash.vue'
import ShipperComponentModal from '@/components/ShipperComponentModal.vue'
import ExportDetailsModal from '@/components/ExportDetailsModal.vue'
import PriceHistoryModal from '@/components/pricing/PriceHistoryModal.vue'
// Mixins
import { userAccess } from '@/mixins/user-access'
import { displayAlert } from '@/mixins/alert'
import { fullWidthTable } from '@/mixins/table'
export default {
  name: 'Items',
  data () {
    return {
      from: 0,
      pageSize: (this.$auth.tenant === 'jbg') ? 200 : 25,
      loading: false,
      items: [],
      searchParams: {},
      exportParams: {},
      selectedItem: {},
      shipperModal: false,
      showDetailsModal: false,
      showPriceHistoryModal: false,
      splashScreen: false,
      showDiscontinuedItems: false,
      originalUpc: null, // used to format user input upc,
      suggestedUpc: null,
      selectedItemDetails: []
    }
  },
  mixins: [fullWidthTable, userAccess, displayAlert],
  components: { PagingFooter, SearchFilters, ItemMasterSplash, ShipperComponentModal, ExportDetailsModal, PriceHistoryModal },
  watch: {
    searchParams: {
      handler (newValue) {
        if (newValue) {
          if (this.page > 1) {
            this.page = 1
          }
        }
      },
      deep: true
    }
  },
  async mounted () {
    this.nonTableHeight = 220
    const dismissedPopup = sessionStorage.getItem('dismissed_popup')
    if (dismissedPopup == null) {
      this.showSplash()
    }
    await Promise.all([
      this.$store.dispatch('setSources'),
      this.$store.dispatch('getPartySourceRelationships'),
      this.$store.dispatch('getUserRelatedParties')
    ]).catch(err => {
      this.loading = false
      this.handleError(err)
    })
    const source_names = this.userSources.map(source => source.source_id)
    await Promise.all([
      this.$store.dispatch('getDepartmentCategoryMapping', { sources: source_names, is_retailer: this.isRetailer }),
      this.$store.dispatch('getCategorySubcategoryMapping', { sources: source_names, is_retailer: this.isRetailer })
    ]).catch(err => {
      this.handleError(err)
    })
    const persistedFilters = sessionStorage.getItem('item_search_filters')
    if (persistedFilters) {
      this.searchParams = { ...this.searchParams, ...JSON.parse(persistedFilters) }
    }
    if (Object.entries(this.searchParams).length > 0) {
      await this.submitSearch()
    }
  },
  computed: {
    disablePrevious () {
      return this.from === 0
    },
    disableNext () {
      return this.items.length < this.pageSize
    },
    headers () {
      return [
        { sortable: false, show: this.showDiscontinuedItems },
        { text: 'Source', value: 'source_name', show: true },
        { text: this.$auth.tenant === 'alliance-retail-group' ? 'Item Code' : 'Item Id', value: 'item_id', show: true },
        { text: 'Description', value: 'description', show: true },
        { text: 'UPC', value: 'upc', show: true },
        { text: 'GTIN', value: 'gtin', show: this.$auth.tenant === 'pricechopper' },
        { text: 'Vendor Id', value: 'vendor_id', show: true, retailerRestricted: this.$auth.tenant === 'jbg' },
        { text: 'Vendor Name', value: 'vendor_name', show: true, retailerRestricted: this.$auth.tenant === 'jbg' },
        { text: 'Department', value: 'department', show: true },
        { text: 'Category', value: 'category', show: true },
        { text: 'Subcategory', value: 'subcategory', show: true },
        { text: 'Status', value: 'status', show: true },
        { text: 'Qty Per Case', value: 'qty_per_case', show: true },
        { text: 'Weight', value: 'weight', show: this.$auth.tenant === 'jbg' },
        { text: 'Cube', value: 'cube', show: this.$auth.tenant === 'jbg' },
        { text: 'Pack Size', value: 'pack_size', show: true },
        { text: this.salePriceText, value: 'sale_price', show: true },
        { text: 'Cost', value: 'cost', show: true },
        { text: 'Components', align: 'center', value: 'components', show: this.$auth.tenant === 'jbg' }
      ]
    },
    computedHeaders () {
      return this.headers.flatMap(item => {
        if (item.show) {
          if (item.retailerRestricted && this.isRetailer) {
            return []
          }
          return { ...item, class: 'accent white--text text-no-wrap' }
        }
        return []
     })
    },
    salePriceText() {
      return this.$auth.tenant === "jbg" ? "SRP" : "Sale Price"
    },
    noDataText () {
      return Object.entries(this.searchParams).length > 0 ? "No data found" : "Please Enter Search Criteria"
    },
    noSearch () {
      // return this.loading || Object.entries(this.searchParams).length === 0    // Disables search if no search terms entered
      return this.loading
    }
  },
  methods: {
    viewItem (mouseevent, row) {
      this.$router.push({ name: 'item', query: { id: row.item.uuid } })
    },
    async handleExportOptions(export_type) {
      this.exportParams = await this.buildItemSearchPayload()
      if (export_type === 'simple') {
        this.exportParams.simple_export = true
        this.exportItems()
      }
      if (export_type === 'detailed') {
        this.showDetailsModal = true
      }
    },
    async exportItems() {
      if (this.selectedItemDetails !== null && this.selectedItemDetails.length > 0) {
        const mappedDetails = [...this.selectedItemDetails].sort((a, b) => a.order - b.order).map(d => d.id)
        this.exportParams.selected_details = mappedDetails
      }
      try {
        const res = await Items.export(this.exportParams)
        if (res) {
          location.href = res
        }
        if (this.showDetailsModal) {
          this.showDetailsModal = false
        }
      } catch (err) {
        this.handleError(err)
      }
    },
    updateUpc(searchSimilar = false) {
      this.clearUpc()
      this.submitSearch(false, searchSimilar)
    },
    clearUpc() {
      this.searchParams.upc = this.originalUpc
      this.originalUpc = null
      this.suggestedUpc = null
    },
    openShipperModal(item) {
      this.selectedItem = item
      this.shipperModal = true
    },
    closeShipperModal() {
      this.selectedItem = {}
      this.shipperModal = false
    },
    onRightClick (mouseevent, row) {
      if (this.$auth.tenant === 'jbg') {
        const string = [this.userStores[0] ? this.userStores[0].id : "", row.item.item_id].join("_")
        navigator.clipboard.writeText(string)
        const url = `https://jbg.${process.env.VUE_APP_PRODUCTS_PORTAL}/order-entry`
        // Keeping the below in for future testing purposes.
        // const url = 'http://localhost:8080/order-entry'
        window.open(url, '_blank').focus()
        mouseevent.preventDefault()
      }
    },
    async submitSearch (validateUpc = true, searchSimilarUpc = false) {
      this.loading = true
      const payload = await this.buildItemSearchPayload(validateUpc)
      this.items = []
      let items = []
      try {
        const data = await Items.searchItems(payload, this.from, this.pageSize, this.isRetailer, searchSimilarUpc)
        if (data && data.length > 0) {
          items = data.map(item => {
            item.source_name = this.$store.getters.getSourceName(item.source)
            return item
          })
        }
        this.items = items
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },
    async buildItemSearchPayload (validateUpc = true) {
      let payload = {}
      if (this.isRetailer && this.userStores.length > 0) {
        const catalog_name = this.retailerCatalog
        if (catalog_name) {
          payload.catalog_name = catalog_name
        }
      }
      if (this.$auth.tenant === 'jbg' && this.isRetailer) {
        payload.show_discontinued_items = this.showDiscontinuedItems
      }
      if (Object.entries(this.searchParams).length > 0) {
        let selectedSources = this.searchParams.item_sources ? this.searchParams.item_sources : []
        if (selectedSources.length > 0) {
          payload.sources = selectedSources
        } else if (this.limitAccessByRelatedSources) {
          const sourceIds = this.userSources.map(s => s.source_id)
          payload.sources = [...sourceIds]
        }

        // Handle UPCs without leading zeros or check digit
        if (validateUpc && this.searchParams.upc) {
          const res = await Items.verifyUpc(this.searchParams.upc)
          const { original_is_valid, standardized_upc } = res.standardized_upc

          if (!original_is_valid && standardized_upc) {
            this.originalUpc = this.searchParams.upc
            this.suggestedUpc = standardized_upc
            this.searchParams.upc = standardized_upc
          } else if (!original_is_valid && standardized_upc == null) {
            this.emitAlert(true, 'warning', 'Invalid UPC')
          }
        }
        payload = { ...payload, ...this.searchParams }
      }
      return payload
    },
    handleFilterInput (newValue, callSearch = false) {
      this.searchParams = newValue
      if (callSearch) {
        return (this.page > 1) ? this.page = 1 : this.submitSearch()
      }
    },
    showSplash () {
      this.splashScreen = true
    },
    closeSplash () {
      this.splashScreen = false
      sessionStorage.setItem('dismissed_popup', true)
    },
    showDiscontinuedItemsChange (newValue) {
        localStorage.setItem('showDiscontinuedItems', JSON.stringify(newValue))
        this.submitSearch()
    }
  },
  created() {
    if (this.$auth.tenant === 'jbg') {
      if (this.isAdmin) {
        localStorage.setItem('showDiscontinuedItems', JSON.stringify(true))
      }
      this.showDiscontinuedItems = JSON.parse(localStorage.getItem('showDiscontinuedItems'))
      this.selectedItemDetails = JSON.parse(localStorage.getItem('selectedItemDetails'))
    }
  }
}
</script>
<style>
.theme--light.v-data-table .v-data-table-header th.sortable.accent.white--text .v-data-table-header__icon {
    color: #fff !important;
  }
</style>



// WEBPACK FOOTER //
// src/views/itemMasterList.vue